import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import rootReducer from '@/store/rootReducer';

type RootState = ReturnType<typeof rootReducer>;

interface State {
  /**
   * 엑셀로 추출하기 위한 크루 데이터 목록
   */
  excelSources: Crew.Item[];
  /**
   * 크루 폼 상태 (생성/수정시 사용)
   */
  formData: Crew.FormData;
}

export const getDefaultFormData = (): State['formData'] => ({
  profileImages: [], // nullable, but last id is for crew api, rest ids are for file deletion api
  name: '', // not nullable
  phone: '', // not nullable
  email: '', // nullable
  birthDate: '', // not nullable
  genderCode: '', // not nullable
  recommender: '', // nullable
  memo: '', // nullable
  contractType: NaN, // not nullable
  contractBegin: '', // not nullable
  contractEnd: '', // not nullable
  paymentType: NaN, // not nullable
  payment: NaN, // not nullable
  bankName: '', // not nullable
  bankAccount: '', // not nullable
  bankAccountHolder: '', // not nullable
});

export const getDefaultState = (): State => ({
  excelSources: [],
  formData: getDefaultFormData(),
});

const crewsStore = createSlice({
  name: 'crews',
  initialState: getDefaultState(),
  reducers: {
    /**
     * 하나 이상의 크루를 추출 대상에 추가합니다.
     * 추가한 데이터 중 기존 데이터와 중복이 있을 경우, 상쇄 작업 후 추가합니다.
     */
    addToExcelSources: (
      state,
      { payload }: PayloadAction<Crew.Item[] | Crew.Item>
    ) => {
      const hashTable = Object.create(null);
      state.excelSources.concat(payload).forEach((excelSource) => {
        if (!hashTable[excelSource.id]) {
          hashTable[excelSource.id] = excelSource;
        }
      });
      state.excelSources = Object.values(hashTable);
    },
    /**
     * 하나의 크루를 추출 대상에서 제거합니다.
     */
    removeFromExcelSource: (state, { payload }: PayloadAction<Crew.Item>) => {
      state.excelSources = state.excelSources.filter(
        (excelSource) => excelSource.id !== payload.id
      );
    },
    /**
     * 추출 대상 목록을 초기화합니다.
     */
    clearExcelSources: (state) => {
      if (state.excelSources.length > 0) {
        const { excelSources } = getDefaultState();
        state.excelSources = excelSources;
      }
    },
    /**
     * 크루 폼 데이터를 업데이트 합니다.
     */
    updateCrewForm: (state, { payload }) => {
      state.formData = { ...state.formData, ...payload };
    },
    /**
     * 크루 폼 데이터를 초기화합니다.
     */
    clearCrewForm: (state) => {
      const { formData } = getDefaultState();
      state.formData = formData;
    },
  },
});

export const crewExcelSourcesSelector = (state: RootState) =>
  state.crews.excelSources;

export const crewFormDataSelector = (state: RootState) => state.crews.formData;

export const crewsStoreActions = crewsStore.actions;

export default crewsStore.reducer;
